import { ObjectId } from 'lib_ui-services';
import lodash from 'lodash';

const { isEqual, omit } = lodash;

// These events can start or finish displaying a form in ActiveRecord.
const ACTIVATION_VERBS = ['copy', 'edit', 'view', 'new'];
const FORM_COMPLETION_VERBS = ['cancel', 'submit', 'remove'];

/**
 * Creates the formInstanceId used to identify one displayed form.
 *
 * ActiveRecord creates a new ID when it displays a different form. It keeps the
 * existing ID when record data refreshes or the same copy, edit, view, or new event repeats.
 *
 * @returns {string} A formInstanceId for the displayed form.
 */
export function createFormInstanceId() {
    return new ObjectId().toString();
}

/**
 * Calculates the ActiveRecord value after a successful form event.
 *
 * An event from a form that is no longer displayed leaves ActiveRecord unchanged.
 * Automatic refreshes replace the record data without reopening the form, which
 * preserves state held below the detail pane, such as the selected History tab.
 *
 * @param {object} previousActiveRecord - The form currently displayed by the provider.
 * @param {object} payload - The successful event payload.
 * @param {object} context - The successful event context.
 * @param {string} verb - The event verb handled by ActiveRecord.
 * @returns {object} The next ActiveRecord value, or the current value when the event
 * came from a form that is no longer displayed.
 */
export function getNextActiveRecord(previousActiveRecord, payload, context, verb) {
    if (shouldIgnoreActiveRecordEvent(previousActiveRecord, payload, context, verb)) {
        return previousActiveRecord;
    }

    if (context?.formRefreshInstanceId) {
        // DetailPane copied the displayed form's formInstanceId before requesting this
        // refresh. Replace only the record data so the same form and nested tab remain open.
        return {
            ...previousActiveRecord,
            record: payload.record,
            oldRecord: payload.oldRecord
        };
    }

    const nextActiveRecord = {
        ...payload,
        namespace: previousActiveRecord.namespace,
        relation: previousActiveRecord.relation
    };
    if (ACTIVATION_VERBS.includes(verb)) {
        // If the same copy, edit, view, or new event repeats, keep the existing object
        // and formInstanceId so ActiveRecord consumers do not rerender unnecessarily.
        if (isEqual(omit(previousActiveRecord, ['formInstanceId']), nextActiveRecord)) {
            return previousActiveRecord;
        }
        nextActiveRecord.formInstanceId = createFormInstanceId();
    } else if (verb === 'submit' && payload?.keepOpenAfterSubmit) {
        // A keep-open submit saves the record without closing its form. Keep the same
        // formInstanceId and form type while setting isNew to false.
        nextActiveRecord.type = previousActiveRecord.type;
        nextActiveRecord.isNew = false;
        nextActiveRecord.activationVerb = previousActiveRecord.activationVerb;
        nextActiveRecord.formInstanceId = previousActiveRecord.formInstanceId;
    }
    return nextActiveRecord;
}

/**
 * Returns the detail-pane event that describes a completed ActiveRecord change.
 *
 * enterDetail is returned when an accepted copy, edit, view, or new action displays
 * a record. leaveDetail is returned when cancel, submit, or remove closes the displayed
 * record. No event is returned when the action is ignored, the form stays open, or only
 * the displayed record's data changes.
 *
 * @param {object} previousActiveRecord - The active record before the transition.
 * @param {object} nextActiveRecord - The active record after the event.
 * @param {object} context - The original event context.
 * @param {string} verb - The original event verb.
 * @returns {{payload: object, context: object}|undefined}
 */
export function getDetailLifecycleEvent(previousActiveRecord, nextActiveRecord, context, verb) {
    if (previousActiveRecord === nextActiveRecord || context?.formRefreshInstanceId) return;

    if (ACTIVATION_VERBS.includes(verb) && nextActiveRecord.record != null) {
        return createDetailLifecycleEvent('enterDetail', nextActiveRecord, verb);
    }
    if (
        FORM_COMPLETION_VERBS.includes(verb) &&
        previousActiveRecord.record != null &&
        nextActiveRecord.record == null
    ) {
        return createDetailLifecycleEvent('leaveDetail', previousActiveRecord, verb);
    }
}

/**
 * Reports whether an event was started by a form that is no longer displayed.
 *
 * ActiveRecord calls this before requesting dirty-form confirmation. The provider also
 * verifies that the displayed form did not change while that confirmation was open.
 *
 * @param {object} activeRecord - The form currently displayed by the provider.
 * @param {object} payload - The successful event payload.
 * @param {object} context - The successful event context.
 * @param {string} verb - The event verb handled by ActiveRecord.
 * @returns {boolean}
 */
export function shouldIgnoreActiveRecordEvent(activeRecord, payload, context, verb) {
    return (
        isStaleAutomaticRefresh(activeRecord, context) ||
        isStaleActivation(activeRecord, context, verb) ||
        isStaleFormCompletion(activeRecord, payload, context, verb)
    );
}

function createDetailLifecycleEvent(verb, activeRecord, sourceVerb) {
    const { namespace, relation, formInstanceId } = activeRecord;
    return {
        payload: activeRecord,
        // sourceVerb records which form event caused enterDetail or leaveDetail.
        context: { verb, namespace, relation, status: 'success', sourceVerb, formInstanceId }
    };
}

function isStaleAutomaticRefresh(activeRecord, context) {
    if (context?.formRefreshInstanceId == null) return false;

    // DetailPane copies formInstanceId before requesting the refresh. Ignore the result
    // if its form closed or another form opened before the request completed.
    return activeRecord.record == null || context.formRefreshInstanceId !== activeRecord.formInstanceId;
}

function isStaleActivation(activeRecord, context, verb) {
    // A copy, edit, view, or new event may finish after another form opens. If the event
    // has a formInstanceId, it must match the form that ActiveRecord still displays.
    return (
        ACTIVATION_VERBS.includes(verb) &&
        context?.formInstanceId != null &&
        context.formInstanceId !== activeRecord.formInstanceId
    );
}

function isStaleFormCompletion(activeRecord, payload, context, verb) {
    if (!FORM_COMPLETION_VERBS.includes(verb)) return false;
    // Remove events are shared by every form for the same record type. Ignore a
    // removal triggered elsewhere when it names a different record.
    if (
        verb === 'remove' &&
        payload?.removedRecordId != null &&
        activeRecord.record?._id != null &&
        String(payload.removedRecordId) !== String(activeRecord.record._id)
    ) {
        return true;
    }
    // Submit success carries formInstanceId in the payload. Cancel and remove normally
    // carry it in event context. Ignore either event when its ID differs from ActiveRecord.
    const formInstanceId = payload?.formInstanceId ?? context?.formInstanceId;
    return formInstanceId != null && formInstanceId !== activeRecord.formInstanceId;
}
